import React, { useEffect, useState } from "react";
import {
  ArrowLeft,
  ArrowUpCircle,
  ArrowDownCircle,
  Banknote,
  MoreVertical,
  ChevronRight,
  Search,
} from "lucide-react";
import { useSelector } from "react-redux";
import { getWalletCustomerDetails } from "../../services/commonServices";
import { useParams } from "react-router-dom";
import Utils from "../../utils";
import { notify } from "../../components/Common/Alerts";
import { decryptData } from "../../utils/crypto";
import { formatCurrencyINR } from "../../utils/function";

const WalletStoreDetails = () => {
  let auth = useSelector((x) => x.auth.value);
  const { uuid } = useParams();

  const [transactionsDetails, setTransactionsDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!uuid) return;
    setIsLoading(true);
    const data = {
      store_id: uuid,
    };


    getWalletCustomerDetails(data)
      .then((response) => {
        const decrypt = decryptData(response.data.data);
        setTransactionsDetails(decrypt.data);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        notify.error(Utils.getErrorMessage(err));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [uuid]);

  const filteredTransactions = transactionsDetails?.list?.filter((item) =>
    item?.order_number?.toString().toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="p-4 md:p-8 bg-gray-50 min-h-screen font-sans">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-slate-500 hover:text-orange-600 font-semibold text-sm mb-4 transition-colors group"
          >
            <div className="p-2 bg-white rounded-xl shadow-sm border border-slate-100 group-hover:border-orange-100 group-hover:bg-orange-50 transition-all">
              <ArrowLeft size={18} />
            </div>
            Back to Wallet
          </button>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-slate-800 tracking-tight">
                Transaction History
              </h1>
              <p className="text-slate-500 text-sm mt-1">
                Detailed records of your store's cash flow
              </p>
            </div>

            <div className="relative hidden md:block">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Search by Order ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-2xl text-sm focus:ring-4 focus:ring-orange-500/10 focus:border-orange-500 outline-none w-72 transition-all shadow-sm"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-[32px] shadow-sm border border-slate-100 overflow-hidden">
          <div className="hidden md:block">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-100">
                  <th className="px-8 py-5 text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                    Type
                  </th>
                  <th className="px-6 py-5 text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                    Order Details
                  </th>
                  <th className="px-6 py-5 text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                    Date & Time
                  </th>
                  <th className="px-6 py-5 text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                    Method
                  </th>
                  <th className="px-6 py-5 text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filteredTransactions?.length > 0 ? (
                  filteredTransactions?.map((item, index) => (
                    <tr
                      key={index}
                      className="hover:bg-slate-50/50 transition-colors group"
                    >
                      <td className="px-8 py-4">
                        <div
                          className={`w-11 h-11 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-105 ${
                            item.type_name === "In"
                              ? "bg-green-50 text-green-600"
                              : "bg-red-50 text-red-600"
                          }`}
                        >
                          {item.type_name === "In" ? (
                            <ArrowUpCircle size={24} />
                          ) : (
                            <ArrowDownCircle size={24} />
                          )}
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1.5">
                          <span className="font-extrabold text-slate-800 text-[15px] tracking-tight">
                            #{item.order_number}
                          </span>

                          <div className="flex">
                            <span
                              className={`
                  ${item.type_name === "In" ? "bg-green-500" : "bg-red-500"} 
                  text-white text-[10px] font-black px-2 py-0.5 rounded shadow-sm uppercase tracking-wider
                `}
                            >
                              {item.type_name}
                            </span>
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-500 font-medium">
                        {item.created_at}
                      </td>

                      <td className="px-6 py-4">
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-slate-100 rounded-lg text-slate-600 font-bold text-[11px] uppercase">
                          <Banknote size={14} className="text-slate-400" />
                          {item.method_name}
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <span
                          className={`text-lg font-black ${
                            item.type_name === "In"
                              ? "text-green-600"
                              : "text-red-600"
                          }`}
                        >
                          +{formatCurrencyINR(item.amount)}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="5"
                      className="text-center py-10 text-slate-400 font-medium"
                    >
                      No transactions found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="md:hidden">
            <div className="p-5 border-b border-slate-50 flex justify-between items-center bg-slate-50/30">
              <h3 className="font-bold text-slate-800 text-sm uppercase tracking-wide">
                Recent Records
              </h3>
              <span className="text-[10px] bg-orange-500 text-white px-2 py-1 rounded-lg font-black shadow-sm">
                {transactionsDetails?.list?.length} TOTAL
              </span>
            </div>
            <div className="divide-y divide-slate-50 px-2">
              {transactionsDetails?.list?.length > 0 ? (
                transactionsDetails?.list?.map((item) => (
                  <div
                    key={item.id}
                    className="p-4 flex items-center justify-between hover:bg-slate-50 rounded-2xl transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm ${
                          item.type_name === "In"
                            ? "bg-green-50 text-green-600"
                            : "bg-red-50 text-red-600"
                        }`}
                      >
                        {item.type_name === "In" ? (
                          <ArrowUpCircle size={26} />
                        ) : (
                          <ArrowDownCircle size={26} />
                        )}
                      </div>

                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-slate-800">
                            #{item.order_number}
                          </span>
                          <span className="bg-green-500 text-white text-[8px] px-1.5 py-0.5 rounded font-black uppercase shadow-sm">
                            {item.type_name}
                          </span>
                        </div>

                        <p className="text-[11px] text-slate-400 font-medium mt-1">
                          {item.created_at}
                        </p>
                      </div>
                    </div>

                    <div className="text-right">
                      <p
                        className={`font-black text-base ${
                          item.type_name === "In"
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        +₹{item.amount}
                      </p>

                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                        {item.method_name}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center text-slate-400">
                  <p className="font-semibold text-slate-500">
                    No transactions found
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Footer Action */}
          {/* <div className="p-4 bg-slate-50/50">
            <button className="w-full py-4 flex items-center justify-center gap-2 text-sm font-bold text-slate-500 hover:text-orange-600 bg-white md:bg-transparent rounded-2xl md:rounded-none shadow-sm md:shadow-none border border-slate-100 md:border-none transition-all group">
              View Full Statement
              <ChevronRight
                size={18}
                className="group-hover:translate-x-1 transition-transform"
              />
            </button>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default WalletStoreDetails;
